/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useMemo} from 'react'
// although unused it is imported to fix the import order
import './url'
import {getSites, resolveSiteFromUrl} from './site-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {decodeUrlLocale, encodeUrlLocale} from './locale'
import logger from './logger-instance'

/**
 * Call requestIdleCallback in supported browsers.
 *
 * https://developers.google.com/web/updates/2015/08/using-requestidlecallback
 * http://caniuse.com/#feat=requestidlecallback
 */
export const requestIdleCallback = (fn) => {
    if ('requestIdleCallback' in window) {
        return window.requestIdleCallback(fn)
    } else {
        return setTimeout(() => fn(), 1)
    }
}

export const watchOnlineStatus = (callback, win = window) => {
    const off = () => callback(false)
    const on = () => callback(true)
    win.addEventListener('offline', off)
    win.addEventListener('online', on)
    const unsubscribe = () => {
        win.removeEventListener('offline', off)
        win.removeEventListener('online', on)
    }
    return unsubscribe
}

/**
 * Compares the primary fields of two address objects to determine if they match.
 * @param {Object} addr1
 * @param {Object} addr2
 * @returns {boolean}
 */
export const isMatchingAddress = (addr1, addr2) => {
    const normalize = (addr) => {
        const {id, addressId, _type, preferred, creationDate, lastModified, ...normalized} = addr
        return normalized
    }
    return shallowEquals(normalize(addr1), normalize(addr2))
}

/**
 * Performs a shallow comparison on two objects
 * @param {Object} a
 * @param {Object} b
 * @returns {boolean}
 */
export const shallowEquals = (a, b) => {
    for (let key in a) {
        if (!(key in b) || a[key] !== b[key]) {
            return false
        }
    }
    for (let key in b) {
        if (!(key in a) || a[key] !== b[key]) {
            return false
        }
    }
    return true
}

/**
 * No operation function. You can use this
 * empty function when you wish to pass
 * around a function that will do nothing.
 * Usually used as default for event handlers.
 */
export const noop = () => {}

/**
 * Flattens a tree data structure into an array.
 * @param {*} node
 * @returns
 */
export const flatten = (node, key = 'children') => {
    const children = (node[key] || []).reduce((a, b) => {
        return Array.isArray(b[key]) && !!b[key].length
            ? {...a, ...flatten(b, key)}
            : {...a, [b.id]: b}
    }, {})

    return {
        [node.id]: node,
        ...children
    }
}

/**
 * Check the current execution environment
 * is client side or server side
 * @returns Boolean
 */
export const isServer = typeof window === 'undefined'

/**
 * This utility function determines if the app has finished hydration
 * @return {boolean}
 */
export const isHydrated = () => typeof window !== 'undefined' && !window.__HYDRATING__

/**
 * Constructs a redirectURI by combining `appOrigin` with `redirectPath`.
 * Ensures that `redirectPath` starts with a '/'.
 * Returns an empty string if `redirectPath` is falsy.
 *
 * @param {*} appOrigin
 * @param {*} redirectPath - relative redirect path
 * @returns redirectURI to be passed into the social login flow
 */
export const buildRedirectURI = (appOrigin = '', redirectPath = '') => {
    if (redirectPath) {
        const path = redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`
        return `${appOrigin}${path}`
    } else {
        return ''
    }
}

/**
 * retrieves an item from session storage
 * @param {string} key
 * @returns JSON | undefined
 */
export const getSessionJSONItem = (key) => {
    if (isServer) {
        return undefined
    }
    const item = window.sessionStorage.getItem(key)
    if (item) {
        return JSON.parse(item)
    } else {
        return undefined
    }
}
/**
 * sets an item in session storage
 * @param {string} key
 * @param {string} value
 */
export const setSessionJSONItem = (key, value) => {
    window.sessionStorage.setItem(key, JSON.stringify(value))
}

/**
 * clears an item in session storage
 * @param {string} key
 */
export const clearSessionJSONItem = (key) => {
    window.sessionStorage.removeItem(key)
}

/**
 * escapes special regex characters
 * @param {string} str
 * @returns escaped string
 */
export const escapeRegexChars = (str) => str.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')

/**
 * bolds a substring of a string by adding <b> tags
 * @param {string} str
 * @param {string} substr
 * @returns stringified HTML Node
 */
export const boldString = (str, substr) => {
    return str?.replace(RegExp(escapeRegexChars(substr.trim()), 'gi'), '<b>$&</b>')
}

/**
 * Capitalizes the words in a string
 * @param {string} text
 * @returns capitalized text
 */
export const capitalize = (text) => {
    return text
        .toLowerCase()
        .split(' ')
        .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
        .join(' ')
}

const safeToCamel = (str) => {
    if (str.startsWith('_') || str.startsWith('c_')) {
        return str
    }
    return str.replace(/([-_][a-z])/gi, ($1) => {
        return $1[1].toUpperCase()
    })
}

const isPlainObject = (obj) => {
    return obj === Object(obj) && !Array.isArray(obj) && typeof obj !== 'function'
}

export const keysToCamel = (obj) => {
    if (isPlainObject(obj)) {
        const n = {}

        Object.keys(obj).forEach((k) => {
            n[safeToCamel(k)] = keysToCamel(obj[k])
        })

        return n
    } else if (Array.isArray(obj)) {
        return obj.map(keysToCamel)
    }

    return obj
}

/**
 * This function return the identifiers (site and locale) from the given url
 * The site will always go before locale if both of them are presented in the pathname
 * @param path {string}
 * @returns {{siteRef: string, localeRef: string}} - site and locale reference (it could either be id or alias)
 */
export const getParamsFromPath = (path) => {
    const {pathname, search} = getPathnameAndSearch(path)

    const config = getConfig()
    const {pathMatcher, searchMatcherForSite, searchMatcherForLocale} = getConfigMatcher(config)
    const pathMatch = pathname.match(pathMatcher)
    const searchMatchForSite = search.match(searchMatcherForSite)
    const searchMatchForLocale = search.match(searchMatcherForLocale)

    // the value can only either in the path or search query param, there will be no overridden
    const siteRef = pathMatch?.groups.site || searchMatchForSite?.groups.site

    const localeRef = decodeUrlLocale(
        pathMatch?.groups.locale || searchMatchForLocale?.groups.locale
    )
    return {siteRef, localeRef}
}

/**
 * This function returns the url config from the current configuration
 * @return {object} - url config
 */
export const getUrlConfig = () => {
    const {app} = getConfig()
    if (!app.url) {
        throw new Error('Cannot find `url` key. Please check your configuration file.')
    }
    return app.url
}

/**
 * Given your application's configuration this function returns a set of regular expressions used to match the site
 * and locale references from an url.
 * @param config
 * @return {{searchMatcherForSite: RegExp, searchMatcherForLocale: RegExp, pathMatcher: RegExp}}
 */
export const getConfigMatcher = (config) => {
    if (!config) {
        throw new Error('Config is not defined.')
    }

    const allSites = getSites()
    const siteIds = []
    const siteAliases = []
    const localesIds = []
    const localeAliases = []
    allSites.forEach((site) => {
        siteAliases.push(site.alias)
        siteIds.push(site.id)
        const {l10n} = site
        l10n.supportedLocales.forEach((locale) => {
            localesIds.push(locale.id)
            localeAliases.push(locale.alias)
        })
    })
    const sites = [...siteIds, ...siteAliases].filter(Boolean)
    const locales = [...localesIds, ...localeAliases].filter(Boolean).map(encodeUrlLocale)

    // prettier-ignore

    const searchPatternForSite = `site=(?<site>${sites.join('|')})`
    // prettier-ignore
    // eslint-disable-next-line
    const pathPattern = `(?:\/(?<site>${sites.join('|')}))?(?:\/(?<locale>${locales.join("|")}))?(?!\\w)`
    // prettier-ignore

    const searchPatternForLocale = `locale=(?<locale>${locales.join('|')})`
    const pathMatcher = new RegExp(pathPattern)
    const searchMatcherForSite = new RegExp(searchPatternForSite)
    const searchMatcherForLocale = new RegExp(searchPatternForLocale)
    return {
        pathMatcher,
        searchMatcherForSite,
        searchMatcherForLocale
    }
}

/**
 * Given a site and a locale reference, return the locale object
 * @param site - site to look for the locale
 * @param localeRef - the locale ref to look for in site supported locales
 * @return {object|undefined}
 */
export const getLocaleByReference = (site, localeRef) => {
    if (!site) {
        throw new Error('Site is not defined. It is required to look for locale object')
    }
    return site.l10n.supportedLocales.find(
        (locale) => locale.id === localeRef || locale.alias === localeRef
    )
}

/**
 * Determine the locale object from an url
 * If the localeRef is not found from the url, set it to default locale of the current site
 * and use it to find the locale object
 *
 * @param url
 * @return {Object} locale object
 */
export const resolveLocaleFromUrl = (url) => {
    if (!url) {
        throw new Error('URL is required to look for the locale object')
    }
    let {localeRef} = getParamsFromPath(url)
    const site = resolveSiteFromUrl(url)
    const {supportedLocales} = site.l10n
    // if no localeRef is found, use the default value of the current site
    if (!localeRef) {
        localeRef = site.l10n.defaultLocale
    }
    const locale = supportedLocales.find(
        (locale) => locale.alias === localeRef || locale.id === localeRef
    )
    if (locale) {
        return locale
    }
    // if locale is not defined, use default locale as fallback value
    const defaultLocale = site.l10n.defaultLocale
    return supportedLocales.find(
        (locale) => locale.alias === defaultLocale || locale.id === defaultLocale
    )
}

/**
 * helper function to check if item is an object
 * @returns {boolean} true if item is an object
 */
const isObject = (item) => item && typeof item === 'object' && !Array.isArray(item)

/**
 * A util to deep merge objects
 * @param {object} target the object to copy to
 * @param {object} sources the objects to copy from
 * @returns {object} the result after merging sources into target
 */
export const deepMerge = (target, ...sources) => {
    if (!sources.length) return target
    const source = sources.shift()

    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key])) {
                if (!target[key])
                    Object.assign(target, {
                        [key]: {}
                    })
                if (source[key] instanceof RegExp) {
                    target[key] = new RegExp(source[key].source, source[key].flags)
                } else {
                    deepMerge(target[key], source[key])
                }
            } else {
                Object.assign(target, {
                    [key]: source[key]
                })
            }
        }
    }

    return deepMerge(target, ...sources)
}

/**
 * Convert a camelCase string into kebab-case
 * @param {string} str
 * @returns {string}
 */
export const camelToKebabCase = (str) => {
    return str.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)
}

/**
 * Convert a kebab-case to camelCase
 * @param {string} str
 * @returns {string}
 */
export const kebabToCamelCase = (str) => {
    return str.replace(/(-[a-z])/g, (group) => group.substring(1).toUpperCase())
}

/**
 * Calculates the difference between current date and passed date
 * @param {string} date
 * @returns {object}
 */
export const getDateDifference = (date) => {
    // Convert the date string to a Date object
    const passedDate = new Date(date)

    // Get the current date
    const currentDate = new Date()

    // Calculate the difference in milliseconds
    const differenceMs = currentDate - passedDate

    // Convert the difference to seconds, minutes, hours, and days
    const differenceSeconds = Math.floor(differenceMs / 1000)
    const differenceMinutes = Math.floor(differenceSeconds / 60)
    const differenceHours = Math.floor(differenceMinutes / 60)
    const differenceDays = Math.floor(differenceHours / 24)

    // Return an object with the differences
    return {
        differenceMs,
        differenceSeconds,
        differenceMinutes,
        differenceHours,
        differenceDays
    }
}

/**
 * Check if the ref is a react ref like object
 * @param {*} ref
 * @returns {boolean}
 */
export function isRefObject(ref) {
    return (
        ref != null &&
        typeof ref === 'object' &&
        Object.prototype.hasOwnProperty.call(ref, 'current')
    )
}

/**
 * Detects if we are running in an iframe.
 * @return {boolean}
 */
export const detectInIframe = () => typeof window !== 'undefined' && window.parent !== window.self

/**
 * Try to parse a string to json (arrays or simple types will not be parsed)
 * @param {string} jsonString
 * @returns {object|null}
 */
export function tryParseJSONObject(jsonString) {
    try {
        const o = JSON.parse(jsonString)
        if (o && typeof o === 'object') {
            return o
        }
    } catch (e) {
        console.error('Error parsing string to object', e)
        return null
    }
}

export function useGetHeaderLinks(headerLinksTemplate) {
    return useMemo(() => {
        const regex = /json-header-links.*(\[.*\])/s
        const m = regex.exec(headerLinksTemplate?.c_body)
        return m !== null ? tryParseJSONObject(m[1]) : []
    }, [headerLinksTemplate?.c_body])
}

/**
 * Log an error message including the response if available
 * @param {string} msg
 * @param {Error} error
 * @return {Promise<void>}
 */
export const logQueryError = async (msg, error) => {
    // fail fast if development logs are not enabled in browser, but allow server side logging
    if (!logger.isEnabled()) {
        return
    }
    if (error.response) {
        if (error.response.text && !error.response.bodyUsed) {
            try {
                // use a clone to avoid consuming the response
                const responseText = await error.response.clone().text()
                try {
                    const responseJson = JSON.parse(responseText)
                    logger.error(msg, error.response.url, responseJson, error)
                } catch (e) {
                    logger.warn('Error parsing response text', e, 'for', error)
                    logger.error(msg, error.response.url, responseText, error)
                }
            } catch (e) {
                logger.warn('Error loading response text', e, 'for', error)
                logger.error(msg, error.response.url, error)
            }
        } else {
            logger.error(msg, error.response.url, error)
        }
    } else {
        logger.error(msg, error, new Error('already used'))
    }
}

/**
 * The code calculates the net price from a given gross price (including VAT) and rounds the result to ensure accuracy
 * 1) first removes the decimal places by multiplying it by 100, which helps to avoid rounding errors with decimal values
 * 2) then calculates the net price by dividing the gross amount by (1 + vatRate), assuming vatRate is given as a decimal (e.g., 0.19 for 19% VAT)
 * 3) finally, it rounds the result to the nearest whole number and converts it back to with decimal places by dividing by 100
 *
 *
 * @param {number} grossPrice
 * @param {number} vatRate
 * @return {number}
 */
export function calculateNetPrice(grossPrice, vatRate) {
    if (isNaN(grossPrice) || grossPrice === null || isNaN(vatRate) || vatRate === null) return null
    return Math.round((grossPrice * 100) / (1 + vatRate)) / 100
}

/**
 * Handles a <tel> element in property messages
 *
 * Takes a pipe-separated string of two values: the telephone number and the display text
 * e.g., "123-456-7890|John Doe"
 *
 * If the string contains only one value, the display text will be the same as the telephone number
 *
 * @param {string[]} chunks A pipe-separated string of two values: the telephone number and the display text.
 *  A chunk is a string in the array.
 * @return {ReactElement} A link element with the telephone number as the href and the display text as the children
 *  or an empty string if the tag is invalid
 */
export const resolveTel = (chunks) => {
    try {
        const parts = chunks[0].split('|')
        return <a href={`tel:${parts[0]}`}>{parts.length > 1 ? parts[1] : parts[0]}</a>
    } catch {
        return ''
    }
}

/**
 * Extract pathname and search params from a given url
 * @private
 * @param url
 * @returns {{search: string, pathname: string}}
 */
export const getPathnameAndSearch = (url) => {
    // since url is a partial url, we pass in a dummy domain to create a validate url to pass into URL constructor
    const {pathname, search} = new URL(url, 'https://www.some-domain.com')
    return {pathname, search}
}

/**
 * call the googlemaps Geocode Api to get missing lat & lng for an address
 * @private
 * @param data - location object
 * @param getGoogleMapsAPI
 * @returns {{data: object, latitude: number, longitude: number}} - return original object with added lat & lng
 */
export async function fillMissingCoordinates(data, getGoogleMapsAPI) {
    const hasLat = data.latitude != null && data.latitude !== ''
    const hasLng = data.longitude != null && data.longitude !== ''

    if (hasLat && hasLng) return data

    const googleMapsAPI = (await getGoogleMapsAPI()) || {}

    if (!googleMapsAPI) {
        throw new Error('Google Maps API unavailable.')
    }

    const location = await new Promise((resolve, reject) => {
        const geocoder = new googleMapsAPI.Geocoder()
        geocoder.geocode({address: data.address}, (results, status) => {
            if (status !== 'OK' && status !== 'ZERO_RESULTS') {
                return reject(status || 'UNKNOWN_ERROR')
            }

            const location = results?.[0]?.geometry?.location
            resolve(location)
        })
    })

    return {
        ...data,
        latitude: location?.lat() || '',
        longitude: location?.lng() || ''
    }
}

/**
 * Checks if a link is external (different origin).
 *
 * @param {string} link
 * @returns {boolean}
 */
export function isExternalLink(link) {
    try {
        // Relative path -> Not external
        if (!/^[a-z][a-z0-9+.-]*:/.test(link) && !link.startsWith('//')) {
            return false
        }

        // Normalize absolute / protocol-relative URLs
        const url = new URL(link, window.location.href)
        return url.origin !== window.location.origin
    } catch {
        // If URL parsing fails -> internal
        return false
    }
}
